<template>
  <div class="company-settings-container page-container bg-light-gradient">
    <div class="container-fluid py-4 px-xl-5">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0 animate-fade-in display-heading">
          <span class="icon icon-primary icon-circle me-2"><i class="fas fa-building"></i></span> Firmeneinstellungen
        </h1>
        <router-link :to="userIsSuperAdmin ? '/admin/dashboard' : '/company/dashboard'" class="btn btn-subtle btn-with-icon animate-fade-in">
          <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zum Dashboard
        </router-link>
      </div>

      <div class="row g-4">
        <!-- Haupteinstellungen Formular -->
        <div class="col-lg-7">
          <Card animateIn="fade-in-up" class="card-main-settings shadow-sm">
            <div v-if="loading" class="text-center py-5 content-loading">
              <div class="loader loader-lg mb-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Wird geladen...</span>
                </div>
              </div>
              <p class="text-muted">Firmendaten werden geladen...</p>
            </div>
            
            <div v-else class="form-enhanced company-form p-lg-4">
              <div v-if="!companyData.id" class="alert alert-warning mb-4 alert-dismissible fade show">
                <p class="mb-1"><i class="fas fa-exclamation-triangle me-2"></i> Firmendaten nicht vollständig geladen.</p>
                <button type="button" class="btn btn-sm btn-primary mt-2 me-2" @click="reloadCompanyData">
                  <i class="fas fa-sync-alt me-1"></i> Neu laden
                </button>
                <button v-if="isCurrentlyImpersonating" type="button" class="btn btn-sm btn-warning mt-2" @click="loadDirectFromCurrentUser">
                  <i class="fas fa-bolt me-1"></i> Direkt laden
                </button>
                <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
              </div>
              
              <form @submit.prevent="saveSettings">
                
                <!-- Sektion: Basisinformationen -->
                <h5 class="section-title">Basisinformationen</h5>
                <div class="mb-3">
                  <label for="name" class="form-label required">Firmenname</label>
                  <input type="text" class="form-control with-focus-effect" id="name" v-model="companyData.name" required :class="{ 'is-invalid': errors.name }">
                  <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                </div>

                <!-- Sektion: Kontaktdaten -->
                <h5 class="section-title mt-4">Kontaktdaten</h5>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label for="email" class="form-label required">E-Mail-Adresse</label>
                    <input type="email" class="form-control with-focus-effect" id="email" v-model="companyData.email" required :class="{ 'is-invalid': errors.email }">
                    <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label for="phone" class="form-label">Telefon</label>
                    <input type="tel" class="form-control with-focus-effect" id="phone" v-model="companyData.phone" :class="{ 'is-invalid': errors.phone }">
                    <div class="invalid-feedback" v-if="errors.phone">{{ errors.phone }}</div>
                  </div>
                </div>
                <div class="mb-3">
                  <label for="website" class="form-label">Website</label>
                  <input type="url" class="form-control with-focus-effect" id="website" v-model="companyData.website" placeholder="https://www.example.com" :class="{ 'is-invalid': errors.website }">
                  <small class="form-text text-muted">Vollständige URL inkl. http(s)://</small>
                  <div class="invalid-feedback" v-if="errors.website">{{ errors.website }}</div>
                </div>

                <!-- Sektion: Adresse -->
                <h5 class="section-title mt-4">Adresse</h5>
                <div class="mb-3">
                  <label for="street" class="form-label">Straße und Hausnummer</label>
                  <input type="text" class="form-control with-focus-effect" id="street" v-model="companyData.street" :class="{ 'is-invalid': errors.street }">
                  <div class="invalid-feedback" v-if="errors.street">{{ errors.street }}</div>
                </div>
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label for="postal_code" class="form-label">Postleitzahl</label>
                    <input type="text" class="form-control with-focus-effect" id="postal_code" v-model="companyData.postal_code" :class="{ 'is-invalid': errors.postal_code }">
                    <div class="invalid-feedback" v-if="errors.postal_code">{{ errors.postal_code }}</div>
                  </div>
                  <div class="col-md-8 mb-3">
                    <label for="city" class="form-label">Stadt</label>
                    <input type="text" class="form-control with-focus-effect" id="city" v-model="companyData.city" :class="{ 'is-invalid': errors.city }">
                    <div class="invalid-feedback" v-if="errors.city">{{ errors.city }}</div>
                  </div>
                </div>

                <!-- Sektion: Branding -->
                <h5 class="section-title mt-4">Branding</h5>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label d-block">Logo</label>
                    <div class="d-flex align-items-center">
                      <div class="logo-preview-container me-3">
                        <CompanyLogo :company="logoPreview" size="lg" :key="logoUpdateKey" class="rounded border" />
                        <span v-if="logoPreviewFile" class="preview-label">Vorschau</span>
                      </div>
                      <div class="logo-controls d-flex flex-column gap-2">
                        <input type="file" ref="logoInput" class="d-none" accept="image/*" @change="handleLogoUpload">
                        <button type="button" class="btn btn-sm btn-outline-primary btn-with-icon" @click="$refs.logoInput.click()">
                          <span class="icon me-1"><i class="fas fa-upload"></i></span> Hochladen
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-danger btn-with-icon" @click="removeLogo" v-if="logoPreview.logo_url || logoPreviewFile">
                          <span class="icon me-1"><i class="fas fa-trash-alt"></i></span> Entfernen
                        </button>
                      </div>
                    </div>
                    <small class="form-text text-muted mt-2 d-block">Max. 2MB (JPG, PNG, GIF, SVG)</small>
                    <div class="invalid-feedback d-block" v-if="errors.logo">{{ errors.logo }}</div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Unternehmensfarbe</label>
                    <div class="input-group color-input-group">
                      <!-- Versteckter Input -->
                      <input 
                        type="color" 
                        ref="colorInput"
                        class="form-control visually-hidden-color-input" 
                        v-model="companyData.primary_color" 
                        title="Wählen Sie die primäre Farbe"
                      >
                      <!-- Sichtbarer Swatch zum Klicken -->
                      <div 
                        class="custom-color-swatch me-2"
                        :style="{ backgroundColor: companyData.primary_color || '#3490dc' }"
                        @click="triggerColorPicker"
                        role="button"
                        tabindex="0" 
                        @keypress.enter="triggerColorPicker"
                        title="Farbe auswählen"
                      ></div>
                      <!-- Hex Input -->
                      <input type="text" class="form-control with-focus-effect" v-model="companyData.primary_color" placeholder="#RRGGBB" pattern="^#[0-9A-Fa-f]{6}$">
                    </div>
                    <small class="form-text text-muted mt-2 d-block">Wird für Akzente verwendet.</small>
                  </div>
                </div>

                <!-- Sektion: E-Mail Einstellungen -->
                <h5 class="section-title mt-4">E-Mail Benachrichtigungen</h5>
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <div class="setting-switch-card">
                      <div class="form-check form-switch form-switch-lg">
                        <div> <!-- Text-Container -->
                          <label class="form-check-label" for="notifyAdmin">Admin bei neuem Test</label>
                          <small class="form-text text-muted d-block">E-Mail an Admin nach Testabschluss.</small>
                        </div>
                        <input class="form-check-input" type="checkbox" id="notifyAdmin" v-model="companyData.notify_admin_on_completion">
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6 mb-3">
                    <div class="setting-switch-card">
                      <div class="form-check form-switch form-switch-lg">
                        <div> <!-- Text-Container -->
                           <label class="form-check-label" for="sendApplicantThankYou">Dankesmail an Teilnehmer</label>
                           <small class="form-text text-muted d-block">E-Mail an Teilnehmer nach Testabschluss.</small>
                         </div>
                        <input class="form-check-input" type="checkbox" id="sendApplicantThankYou" v-model="companyData.send_applicant_thank_you_email">
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Formularaktionen -->
                <div class="form-actions border-top pt-4 mt-4">
                  <button type="submit" class="btn btn-primary btn-lg btn-with-icon btn-glow-effect me-3" :disabled="saving">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <span v-else class="icon me-2"><i class="fas fa-save"></i></span>
                    Änderungen speichern
                  </button>
                  <button type="button" class="btn btn-outline-secondary btn-lg btn-with-icon" @click="resetForm" :disabled="saving">
                    <span class="icon me-2"><i class="fas fa-undo"></i></span> Zurücksetzen
                  </button>
                </div>
              </form>
            </div>
          </Card>
        </div>
        
        <!-- Vorschau Spalte -->
        <div class="col-lg-5">
          <div class=" preview-sticky-container">
            <Card animateIn="fade-in-up" :style="{ animationDelay: '0.1s' }" class="card-preview shadow-sm">
              <template v-slot:header>
                <h5 class="mb-0"><i class="fas fa-eye me-2"></i> Vorschau</h5>
              </template>
              <div class="preview-content p-3">
                <div class="text-center mb-4">
                  <CompanyLogo :company="logoPreview" size="large" class="mb-3 rounded border shadow-sm" />
                  <h5 class="preview-title mb-1">{{ companyData.name || 'Ihr Firmenname' }}</h5>
                </div>
                <div class="preview-details">
                  <p v-if="companyData.email" class="preview-meta">
                    <i class="fas fa-envelope fa-fw me-2 text-muted"></i> {{ companyData.email }}
                  </p>
                  <p v-if="companyData.phone" class="preview-meta">
                    <i class="fas fa-phone fa-fw me-2 text-muted"></i> {{ companyData.phone }}
                  </p>
                  <p v-if="companyData.website" class="preview-meta">
                    <i class="fas fa-globe fa-fw me-2 text-muted"></i>
                    <a :href="websiteWithProtocol" target="_blank" rel="noopener noreferrer">{{ companyData.website }}</a>
                  </p>
                  <p v-if="companyData.street || companyData.postal_code || companyData.city" class="preview-meta address-preview">
                    <i class="fas fa-map-marker-alt fa-fw me-2 text-muted"></i> 
                    <span>{{ companyData.street }}</span><span v-if="companyData.street">,</span><br>
                    <span class="ms-4 ps-1">{{ companyData.postal_code }} {{ companyData.city }}</span>
                  </p>
                </div>
                
                <hr class="my-4">

                <div class="color-sample mb-3">
                  <label class="form-label small mb-1">Primärfarbe:</label>
                  <div class="d-flex align-items-center">
                    <div class="color-swatch me-2" :style="{ backgroundColor: companyData.primary_color || '#3490dc' }"></div>
                    <span class="color-hex text-muted">{{ companyData.primary_color || '#3490dc' }}</span>
                  </div>
                </div>
                
                <div class="preview-buttons mt-3">
                  <label class="form-label small mb-1">Beispiel-Buttons:</label>
                  <div class="d-flex flex-wrap gap-2">
                    <button class="btn btn-sm btn-with-icon text-white" :style="{ backgroundColor: companyData.primary_color || '#3490dc' }">
                      <span class="icon me-1"><i class="fas fa-check"></i></span> Primary
                    </button>
                    <button class="btn btn-sm btn-outline-secondary btn-with-icon">
                      <span class="icon me-1"><i class="fas fa-times"></i></span> Secondary
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCompanyStore } from '@/stores/company';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import Card from '@/components/ui/Card.vue';
import CompanyLogo from '@/components/ui/CompanyLogo.vue';
import http from '@/services/http';

export default defineComponent({
  name: 'CompanySettings',
  components: {
    Card,
    CompanyLogo
  },
  setup() {
    const router = useRouter();
    const companyStore = useCompanyStore();
    const authStore = useAuthStore();
    const toast = useToast();
    
    // Reaktive Zustände
    const loading = ref(false);
    const saving = ref(false);
    const companyData = ref({
      id: null,
      name: '',
      email: '',
      phone: '',
      website: '',
      street: '',
      postal_code: '',
      city: '',
      logo_url: null,
      primary_color: '#3490dc',
      notify_admin_on_completion: false,
      send_applicant_thank_you_email: false,
      // privacy_policy: ''
    });
    const logoInput = ref(null);
    const logoPreviewFile = ref(null);
    const logoUpdateKey = ref(0);
    const errors = ref({});
    const originalCompanyData = ref(null);
    
    const users = ref([]);
    const userData = ref({
      name: '',
      email: '',
      password: '',
      role: 'user'
    });
    const showUserPassword = ref(false);
    
    // Computed Properties
    const currentCompany = computed(() => companyStore.currentCompany);
    const isSuper = computed(() => authStore.isSuperAdmin);
    const user = computed(() => authStore.getUser);
    
    const userIsSuperAdmin = computed(() => {
      return user.value && user.value.role === 'super_admin';
    });
    
    const websiteWithProtocol = computed(() => {
      if (!companyData.value.website) return '';
      
      // Stelle sicher, dass die Website mit http:// oder https:// beginnt
      if (companyData.value.website.startsWith('http://') || companyData.value.website.startsWith('https://')) {
        return companyData.value.website;
      }
      
      // Ansonsten https:// hinzufügen
      return 'https://' + companyData.value.website;
    });
    
    const logoPreview = computed(() => {
      if (!companyData.value) return {};
      
      // Wenn eine Dateivorschau existiert, erstelle ein Objekt mit einer temporären URL
      if (logoPreviewFile.value) {
        return {
          ...companyData.value,
          logo_url: URL.createObjectURL(logoPreviewFile.value)
        };
      }
      
      // Ansonsten verwende das bestehende Logo
      let logoUrl = companyData.value.logo_url;
      
      if (!logoUrl && companyData.value.logo) {
        // Wenn keine logo_url, aber ein logo-Pfad vorhanden ist, konstruiere URL
        logoUrl = `${window.location.origin}/storage/${companyData.value.logo}`;
      }
      
      return {
        ...companyData.value,
        logo_url: logoUrl
      };
    });
    
    const isUserFormValid = computed(() => {
      return userData.value.name && 
             userData.value.email && 
             userData.value.password && 
             userData.value.password.length >= 8 &&
             userData.value.role;
    });
    
    const isCurrentlyImpersonating = computed(() => {
      return authStore && authStore.isImpersonating;
    });
    
    // Methoden
    const loadCompany = async () => {
      loading.value = true;
      try {
        console.log('loadCompany gestartet, isImpersonating:', authStore.isImpersonating);
        console.log('auth user:', authStore.user);
        console.log('auth company:', authStore.company);
        
        // METHODE 1: Bei Impersonierung die Firmendaten direkt aus dem authStore verwenden
        if (authStore.isImpersonating && authStore.company) {
          console.log('METHODE 1: Verwende Firmendaten aus authStore.company');
          
          // Prüfen, ob es sich um ein verschachteltes Objekt handelt (mehrere Ebenen)
          let company = null;
          
          // Fall 1: Direkt ein Unternehmensobjekt
          if (authStore.company.id) {
            company = authStore.company;
            console.log('Fall 1: Direkt ein Unternehmensobjekt');
          } 
          // Fall 2: Verschachtelt in currentCompany
          else if (authStore.company.currentCompany) {
            if (authStore.company.currentCompany.id) {
              company = authStore.company.currentCompany;
              console.log('Fall 2: Verschachtelt in currentCompany');
            } else if (authStore.company.currentCompany.company && authStore.company.currentCompany.company.id) {
              company = authStore.company.currentCompany.company;
              console.log('Fall 2b: Doppelt verschachtelt in currentCompany.company');
            }
          }
          // Fall 3: Verschachtelt in company
          else if (authStore.company.company) {
            company = authStore.company.company;
            console.log('Fall 3: Verschachtelt in company-Property');
          }
          
          console.log('Extrahierte Firmendaten:', company);
          
          // Nur fortfahren, wenn wir eine gültige Firma mit ID haben
          if (company && company.id) {
            // Daten in lokale Variablen kopieren
            companyData.value = { 
              id: company.id,
              name: company.name || '',
              email: company.email || '',
              phone: company.phone || '',
              website: company.website || '',
              street: company.street || '',
              postal_code: company.postal_code || '',
              city: company.city || '',
              logo: company.logo || null,
              logo_url: company.logo_url || (company.logo ? `${window.location.origin}/storage/${company.logo}` : null),
              primary_color: company.primary_color || '#007bff',
              notify_admin_on_completion: company.notify_admin_on_completion === 1,
              send_applicant_thank_you_email: company.send_applicant_thank_you_email === 1,
              // privacy_policy: company.privacy_policy || ''
            };
            
            // Original-Daten ebenfalls speichern
            originalCompanyData.value = { ...companyData.value };
            
            console.log('Firmendaten aus authStore in Komponente gesetzt:', companyData.value);
            loading.value = false;
            return;
          } else {
            console.warn('Keine gültigen Firmendaten im authStore gefunden');
          }
        }
        
        // METHODE 2: Aus localStorage lesen, falls vorhanden
        if (authStore.isImpersonating) {
          console.log('METHODE 2: Versuche Daten aus localStorage zu lesen');
          const storedCompanyRaw = JSON.parse(localStorage.getItem('company') || 'null');
          
          console.log('Rohe localStorage-Daten:', storedCompanyRaw);
          
          // Prüfen, ob es sich um ein verschachteltes Objekt handelt
          let storedCompany;
          if (storedCompanyRaw && storedCompanyRaw.currentCompany) {
            console.log('Firmendaten sind im currentCompany-Feld verschachtelt');
            storedCompany = storedCompanyRaw.currentCompany;
          } else {
            storedCompany = storedCompanyRaw;
          }
          
          if (storedCompany && storedCompany.id) {
            console.log('Firmendaten aus localStorage gefunden:', storedCompany);
            
            // In den Store setzen
            authStore.setCompany(storedCompany);
            
            // Daten in lokale Variablen kopieren
            companyData.value = { 
              id: storedCompany.id,
              name: storedCompany.name || '',
              email: storedCompany.email || '',
              phone: storedCompany.phone || '',
              website: storedCompany.website || '',
              street: storedCompany.street || '',
              postal_code: storedCompany.postal_code || '',
              city: storedCompany.city || '',
              logo: storedCompany.logo || null,
              logo_url: storedCompany.logo_url || (storedCompany.logo ? `${window.location.origin}/storage/${storedCompany.logo}` : null),
              primary_color: storedCompany.primary_color || '#007bff',
              notify_admin_on_completion: storedCompany.notify_admin_on_completion === 1,
              send_applicant_thank_you_email: storedCompany.send_applicant_thank_you_email === 1,
              // privacy_policy: storedCompany.privacy_policy || ''
            };
            
            // Original-Daten ebenfalls speichern
            originalCompanyData.value = { ...companyData.value };
            
            console.log('Firmendaten aus localStorage in Komponente gesetzt:', companyData.value);
            loading.value = false;
            return;
          }
        }
        
        // METHODE 3: Impersonierungsdaten direkt vom Server laden
        if (authStore.isImpersonating) {
          console.log('METHODE 3: Versuche /api/auth/impersonation-data für Impersonierungsdaten');
          try {
            const response = await http.get('/api/auth/impersonation-data');
            console.log('Impersonation-Data API Antwort:', response.data);
            
            if (response.data && response.data.company) {
              const company = response.data.company;
              
              // In den Store setzen
              authStore.setCompany(company);
              
              // Daten in lokale Variablen kopieren
              companyData.value = { 
                id: company.id,
                name: company.name || '',
                email: company.email || '',
                phone: company.phone || '',
                website: company.website || '',
                street: company.street || '',
                postal_code: company.postal_code || '',
                city: company.city || '',
                logo: company.logo || null,
                logo_url: company.logo_url || (company.logo ? `${window.location.origin}/storage/${company.logo}` : null),
                primary_color: company.primary_color || '#007bff',
                notify_admin_on_completion: company.notify_admin_on_completion === 1,
                send_applicant_thank_you_email: company.send_applicant_thank_you_email === 1,
                // privacy_policy: company.privacy_policy || ''
              };
              
              // Original-Daten ebenfalls speichern
              originalCompanyData.value = { ...companyData.value };
              
              console.log('Firmendaten aus API in Komponente gesetzt:', companyData.value);
              loading.value = false;
              return;
            }
          } catch (apiError) {
            console.error('Fehler beim direkten Laden der Impersonierungsdaten:', apiError);
          }
        }
        
        // METHODE 4: Wenn ein Impersonierungsmodus läuft, versuche die Firma direkt über die Benutzer-ID zu laden
        if (authStore.isImpersonating && authStore.user && authStore.user.company_id) {
          console.log('METHODE 4: Versuche Firma über die Benutzer-ID zu laden:', authStore.user.company_id);
          
          try {
            const companyResponse = await http.get(`/api/companies/${authStore.user.company_id}`);
            console.log('Firma direkt über ID geladen:', companyResponse.data);
            
            // Extrahiere die richtigen Firmendaten
            let company = null;
            
            // Fall 1: Direkt ein Unternehmensobjekt
            if (companyResponse.data && companyResponse.data.id) {
              company = companyResponse.data;
              console.log('API Fall 1: Direkt ein Unternehmensobjekt');
            }
            // Fall 2: Verschachtelt in company
            else if (companyResponse.data && companyResponse.data.company && companyResponse.data.company.id) {
              company = companyResponse.data.company;
              console.log('API Fall 2: Verschachtelt in company-Property');
            }
            
            console.log('Extrahierte API-Firmendaten:', company);
            
            if (company && company.id) {
              // In den Store setzen
              authStore.setCompany(company);
              
              // Daten in lokale Variablen kopieren
              companyData.value = { 
                id: company.id,
                name: company.name || '',
                email: company.email || '',
                phone: company.phone || '',
                website: company.website || '',
                street: company.street || '',
                postal_code: company.postal_code || '',
                city: company.city || '',
                logo: company.logo || null,
                logo_url: company.logo_url || (company.logo ? `${window.location.origin}/storage/${company.logo}` : null),
                primary_color: company.primary_color || '#007bff',
                notify_admin_on_completion: company.notify_admin_on_completion === 1,
                send_applicant_thank_you_email: company.send_applicant_thank_you_email === 1,
                // privacy_policy: company.privacy_policy || ''
              };
              
              // Original-Daten ebenfalls speichern
              originalCompanyData.value = { ...companyData.value };
              
              console.log('Firmendaten direkt über ID geladen:', companyData.value);
              loading.value = false;
              return;
            } else {
              console.warn('Keine gültigen Firmendaten über API geladen');
            }
          } catch (error) {
            console.error('Fehler beim Laden der Firma über die ID:', error);
          }
        }
        
        // METHODE 5: Standardmethode über companyStore und currentCompany
        // Wenn ein Super-Admin und keine Firma ausgewählt, Firmenliste anzeigen
        if (isSuper.value && !currentCompany.value) {
          console.log('METHODE 5: Super-Admin ohne ausgewählte Firma, leite zur Firmenliste weiter');
          router.push('/companies');
          return;
        }
        
        // Ansonsten die aktuelle Firma laden
        if (currentCompany.value) {
          console.log('METHODE 5: Lade ausgewählte Firma mit ID:', currentCompany.value.id);
          await companyStore.fetchCompany(currentCompany.value.id);
          
          // Daten in lokale Variablen kopieren
          companyData.value = { 
            id: currentCompany.value.id,
            name: currentCompany.value.name || '',
            email: currentCompany.value.email || '',
            phone: currentCompany.value.phone || '',
            website: currentCompany.value.website || '',
            street: currentCompany.value.street || '',
            postal_code: currentCompany.value.postal_code || '',
            city: currentCompany.value.city || '',
            logo: currentCompany.value.logo || null,
            logo_url: currentCompany.value.logo_url || (currentCompany.value.logo ? `${window.location.origin}/storage/${currentCompany.value.logo}` : null),
            primary_color: currentCompany.value.primary_color || '#007bff',
            notify_admin_on_completion: currentCompany.value.notify_admin_on_completion === 1,
            send_applicant_thank_you_email: currentCompany.value.send_applicant_thank_you_email === 1,
            // privacy_policy: currentCompany.value.privacy_policy || ''
          };
          
          originalCompanyData.value = { ...companyData.value };
          console.log('Firmendaten in Komponente gesetzt:', companyData.value);
        } else {
          console.error('Keine aktuelle Firma gefunden');
        }
      } catch (error) {
        console.error('Fehler in loadCompany:', error);
        toast.error('Fehler beim Laden der Firmendaten');
      } finally {
        loading.value = false;
      }
    };
    
    const loadCompanyFromData = (data) => {
      console.log('Lade Firmendaten direkt aus übergebenem Datenobjekt:', data);
      
      if (data && data.company) {
        const company = data.company;
        
        // Daten in lokale Variablen kopieren
        companyData.value = { 
          id: company.id,
          name: company.name || '',
          email: company.email || '',
          phone: company.phone || '',
          website: company.website || '',
          street: company.street || '',
          postal_code: company.postal_code || '',
          city: company.city || '',
          logo: company.logo || null,
          logo_url: company.logo_url || (company.logo ? `${window.location.origin}/storage/${company.logo}` : null),
          primary_color: company.primary_color || '#007bff',
          notify_admin_on_completion: company.notify_admin_on_completion === 1,
          send_applicant_thank_you_email: company.send_applicant_thank_you_email === 1,
          // privacy_policy: company.privacy_policy || ''
        };
        
        // Original-Daten ebenfalls speichern
        originalCompanyData.value = { ...companyData.value };
        
        console.log('Firmendaten direkt gesetzt:', companyData.value);
        return true;
      }
      
      console.error('Keine gültigen Firmendaten im übergebenen Objekt gefunden');
      return false;
    };
    
    const saveSettings = async () => {
      saving.value = true;
      errors.value = {};
      
      try {
        // Erstellr ein FormData-Objekt für den Upload von Dateien
        const formData = new FormData();
        
        // Füge alle Felder hinzu
        for (const key in companyData.value) {
          // Überspringe logo_url, da es nicht Teil der Backend-API ist
          if (key !== 'logo_url') {
            const value = companyData.value[key];
            
            // Konvertiere Booleans explizit zu '1' oder '0' (String!) für die API
            if (key === 'notify_admin_on_completion' || key === 'send_applicant_thank_you_email') {
              const apiValue = value === true ? '1' : '0'; // Sende String '1' oder '0'
              formData.append(key, apiValue);
            } else {
              // Füge andere Werte normal hinzu (null wird zu leerem String)
              formData.append(key, value ?? '');
            }
          }
        }
        
        // API-Aufruf zum Speichern der Daten
        const response = await companyStore.updateSettings(formData);
        
        // Aktualisiere lokale Daten mit der Antwort
        if (response) {
          // Aktualisiere Store und lokale Daten
          companyStore.addCompany(response);
          companyStore.setCurrentCompany(response);
          
          // Aktualisiere die lokalen Daten
          companyData.value = { 
            ...companyData.value,
            ...response,
            logo_url: response.logo_url || (response.logo ? `${window.location.origin}/storage/${response.logo}` : null)
          };
          
          // WICHTIG: Aktualisiere den Auth-Store, damit die Änderungen sofort global verfügbar sind
          authStore.setCompany(response);
          
          // Aktualisiere auch den localStorage für sofortige globale Verfügbarkeit
          localStorage.setItem('company', JSON.stringify(response));
          
          // Aktualisiere die Original-Daten
          originalCompanyData.value = { ...companyData.value };
          
          // Erzwinge Neurendern
          logoUpdateKey.value++;
          
          toast.success('Einstellungen gespeichert');
        }
      } catch (error) {
        console.error('Fehler beim Speichern der Einstellungen:', error);
        
        // API-Fehlermeldungen behandeln
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            errors.value = error.response.data.errors;
          } else if (error.response.data.message) {
            toast.error(error.response.data.message);
          }
        } else {
          toast.error('Ein Fehler ist aufgetreten');
        }
      } finally {
        saving.value = false;
      }
    };
    
    const resetForm = () => {
      if (originalCompanyData.value) {
        companyData.value = { ...originalCompanyData.value };
      }
      clearErrors();
    };
    
    const clearErrors = () => {
      errors.value = {};
    };
    
    const handleLogoUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      
      // Dateigrößenbeschränkung (2MB)
      const maxSize = 2 * 1024 * 1024; // 2MB in Bytes
      if (file.size > maxSize) {
        toast.error('Das Logo darf maximal 2MB groß sein');
        event.target.value = null; // Zurücksetzen des Inputs
        return;
      }
      
      // Dateitypüberprüfung
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Nur Bilddateien (JPEG, PNG, GIF, SVG) sind erlaubt');
        event.target.value = null;
        return;
      }
      
      try {
        // FormData für den Upload erstellen
        const formData = new FormData();
        formData.append('logo', file);
        
        // Alle anderen relevanten Firmendaten hinzufügen (MIT Boolean-Konvertierung!)
        for (const key in companyData.value) {
          // Überspringe logo_url, da es nicht Teil der Backend-API ist
          if (key !== 'logo_url') {
            const value = companyData.value[key];
            
            // Konvertiere Booleans explizit zu '1' oder '0' (String!) für die API
            if (key === 'notify_admin_on_completion' || key === 'send_applicant_thank_you_email') {
              const apiValue = value === true ? '1' : '0';
              formData.append(key, apiValue);
            } else {
              // Füge andere Werte normal hinzu (null wird zu leerem String)
              formData.append(key, value ?? '');
            }
          }
        }
        
        // Logo direkt hochladen (ruft updateSettings im Store auf)
        const response = await companyStore.updateSettings(formData);
        
        // Aktualisiere die Firmendaten mit der Antwort
        if (response) {
          // Aktualisiere Store und lokale Daten
          companyStore.addCompany(response);
          companyStore.setCurrentCompany(response);
          
          // Aktualisiere die lokalen Daten
          companyData.value = { 
            ...companyData.value,
            ...response,
            logo_url: response.logo_url || (response.logo ? `${window.location.origin}/storage/${response.logo}` : null)
          };
          
          // Aktualisiere die Original-Daten
          originalCompanyData.value = { ...companyData.value };
          
          // WICHTIG: Aktualisiere den Auth-Store, damit die Änderungen sofort global verfügbar sind
          authStore.setCompany(response);
          
          // Aktualisiere auch den localStorage für sofortige globale Verfügbarkeit
          localStorage.setItem('company', JSON.stringify(response));
          
          // Erzwinge Neurendern
          logoUpdateKey.value++;
          
          toast.success('Logo erfolgreich hochgeladen');
        }
      } catch (error) {
        console.error('Fehler beim Hochladen des Logos:', error);
        
        // API-Fehlermeldungen behandeln
        if (error.response && error.response.data) {
          // === DETAILLIERTE FEHLERMELDUNG LOGGEN ===
          console.error('Backend Validierungsfehler:', error.response.data);
          // === ENDE LOGGING ===
          if (error.response.data.errors) {
            errors.value = error.response.data.errors;
            // Zeige spezifische Fehlermeldungen dem Benutzer
            const errorMessages = Object.values(error.response.data.errors).flat().join(' ');
            toast.error(`Fehler beim Hochladen: ${errorMessages}`);
          } else if (error.response.data.message) {
            toast.error(error.response.data.message);
          }
        } else {
          toast.error('Fehler beim Hochladen des Logos');
        }
      }
      
      // Vorschau-Datei zurücksetzen, da bereits hochgeladen oder fehlgeschlagen
      logoPreviewFile.value = null;
      
      // Setze den Datei-Input zurück
      event.target.value = null;
    };
    
    const removeLogo = async () => {
      // Wenn nur eine Vorschau gelöscht wird, setze nur die Vorschau zurück
      if (logoPreviewFile.value) {
        logoPreviewFile.value = null;
        logoUpdateKey.value++;
        toast.info('Vorschau entfernt');
        return;
      }
      
      // Ansonsten entferne das tatsächliche Logo
      try {
        loading.value = true;
        
        // API-Aufruf zum Entfernen des Logos
        const response = await companyStore.removeCompanyLogo(companyData.value.id);
        
        // Aktualisiere die lokalen Daten
        companyData.value.logo_url = null;
        companyData.value.logo = null;
        
        // WICHTIG: Aktualisiere den Auth-Store und localStorage, falls Daten zurückgegeben wurden
        if (response) {
          authStore.setCompany(response);
          localStorage.setItem('company', JSON.stringify(response));
        } else {
          // Wenn keine Antwort, dann aktualisiere mit den lokalen Daten
          authStore.setCompany(companyData.value);
          localStorage.setItem('company', JSON.stringify(companyData.value));
        }
        
        // Erzwinge Neurendern
        logoUpdateKey.value++;
        
        toast.success('Logo erfolgreich entfernt');
      } catch (error) {
        console.error('Fehler beim Entfernen des Logos:', error);
        toast.error('Fehler beim Entfernen des Logos');
      } finally {
        loading.value = false;
      }
    };
    
    const darkenColor = (hex, percent) => {
      // Konvertiere Hex zu RGB
      let r = parseInt(hex.substring(1, 3), 16);
      let g = parseInt(hex.substring(3, 5), 16);
      let b = parseInt(hex.substring(5, 7), 16);
      
      // Abdunkeln
      r = Math.max(0, Math.floor(r * (100 - percent) / 100));
      g = Math.max(0, Math.floor(g * (100 - percent) / 100));
      b = Math.max(0, Math.floor(b * (100 - percent) / 100));
      
      // Konvertiere zurück zu Hex
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };
    
    const loadDirectFromCurrentUser = async () => {
      console.log('Versuche Firmendaten direkt über /api/user zu laden');
      
      try {
        // Frische Benutzerdaten abrufen 
        const userResponse = await http.get('/api/user');
        console.log('Antwort von /api/user:', userResponse.data);
        
        if (userResponse.data && userResponse.data.company_id) {
          // Firmendaten mit der Benutzer-ID laden
          const companyResponse = await http.get(`/api/companies/${userResponse.data.company_id}`);
          console.log('Antwort von companies API:', companyResponse.data);
          
          if (companyResponse.data) {
            const company = companyResponse.data;
            
            // In den Store setzen
            authStore.setCompany(company);
            
            // Manuelle Zuweisung
            companyData.value = { 
              id: company.id,
              name: company.name || '',
              email: company.email || '',
              phone: company.phone || '',
              website: company.website || '',
              street: company.street || '',
              postal_code: company.postal_code || '',
              city: company.city || '',
              logo: company.logo || null,
              logo_url: company.logo_url || (company.logo ? `${window.location.origin}/storage/${company.logo}` : null),
              primary_color: company.primary_color || '#007bff',
              notify_admin_on_completion: company.notify_admin_on_completion === 1,
              send_applicant_thank_you_email: company.send_applicant_thank_you_email === 1,
              // privacy_policy: company.privacy_policy || ''
            };
            
            // Original-Daten ebenfalls speichern
            originalCompanyData.value = { ...companyData.value };
            
            toast.success('Firmendaten erfolgreich geladen');
            return true;
          }
        }
        
        return false;
      } catch (error) {
        console.error('Fehler beim direkten Laden über /api/user:', error);
        return false;
      }
    };
    
    const reloadCompanyData = async () => {
      toast.info('Lade Firmendaten neu...');
      
      // Versuche zuerst die normale Methode
      await loadCompany();
      
      // Wenn keine Daten geladen wurden, versuche die direkte Methode
      if (!companyData.value.name) {
        console.log('Kein Erfolg bei regulärem Laden, versuche direkte Methoden');
        
        // 1. Versuche impersonation-data
        try {
          const response = await http.get('/api/auth/impersonation-data');
          console.log('Impersonation-Data API Antwort:', response.data);
          
          if (loadCompanyFromData(response.data)) {
            toast.success('Firmendaten erfolgreich geladen');
            return;
          }
        } catch (error) {
          console.error('Fehler beim direkten Laden der Impersonierungsdaten:', error);
        }
        
        // 2. Versuche direktes Laden über den Benutzer
        const directSuccess = await loadDirectFromCurrentUser();
        if (directSuccess) {
          return;
        }
        
        // Wenn immer noch keine Daten, Fehlermeldung
        if (!companyData.value.name) {
          toast.error('Firmendaten konnten nicht geladen werden');
        }
      } else {
        toast.success('Firmendaten erfolgreich geladen');
      }
    };
    
    const triggerColorPicker = () => {
      if (colorInput.value) {
        colorInput.value.click();
      }
    };
    
    // Lebenszyklus-Hooks
    onMounted(async () => {
      console.log('### CompanySettings onMounted BEGINN ###');
      console.log('isImpersonating:', authStore.isImpersonating);
      console.log('authUser:', authStore.user);
      console.log('authCompany direkt:', authStore.company);
      console.log('localStorage.company:', JSON.parse(localStorage.getItem('company') || 'null'));
      
      // Laden der Firmendaten
      try {
        await loadCompany();
        console.log('Nach loadCompany, companyData:', companyData.value);
        
        // Wenn keine Daten geladen wurden und wir impersonieren, manuell aus localStorage laden
        if (authStore.isImpersonating && (!companyData.value || !companyData.value.name)) {
          console.log('Nach loadCompany: Keine Daten. Versuche aus localStorage zu laden.');
          
          const storedCompanyRaw = JSON.parse(localStorage.getItem('company') || 'null');
          console.log('Rohe localStorage-Daten in onMounted:', storedCompanyRaw);
          
          // Prüfen, ob es sich um ein verschachteltes Objekt handelt
          let storedCompany;
          if (storedCompanyRaw && storedCompanyRaw.currentCompany) {
            console.log('Firmendaten sind im currentCompany-Feld verschachtelt');
            storedCompany = storedCompanyRaw.currentCompany;
          } else {
            storedCompany = storedCompanyRaw;
          }
          
          if (storedCompany && storedCompany.id) {
            console.log('Firmendaten aus localStorage gefunden:', storedCompany);
            
            // Direkt ins authStore setzen
            authStore.setCompany(storedCompany);
            
            // Manuelle Zuweisung an companyData
            companyData.value = { 
              id: storedCompany.id,
              name: storedCompany.name || '',
              email: storedCompany.email || '',
              phone: storedCompany.phone || '',
              website: storedCompany.website || '',
              street: storedCompany.street || '',
              postal_code: storedCompany.postal_code || '',
              city: storedCompany.city || '',
              logo: storedCompany.logo || null,
              logo_url: storedCompany.logo_url || (storedCompany.logo ? `${window.location.origin}/storage/${storedCompany.logo}` : null),
              primary_color: storedCompany.primary_color || '#007bff',
              notify_admin_on_completion: storedCompany.notify_admin_on_completion === 1,
              send_applicant_thank_you_email: storedCompany.send_applicant_thank_you_email === 1,
              // privacy_policy: storedCompany.privacy_policy || ''
            };
            
            // Original-Daten ebenfalls speichern
            originalCompanyData.value = { ...companyData.value };
            
            console.log('Firmendaten manuell aus localStorage geladen:', companyData.value);
          } else {
            console.log('Keine Firmendaten im localStorage gefunden.');
            
            // Als letzten Versuch direkt die API aufrufen
            try {
              const response = await http.get('/api/auth/impersonation-data');
              console.log('Direkte API-Antwort:', response.data);
              
              if (response.data && response.data.company) {
                const apiCompany = response.data.company;
                
                // In den Store setzen
                authStore.setCompany(apiCompany);
                
                // Manuelle Zuweisung
                companyData.value = { 
                  id: apiCompany.id,
                  name: apiCompany.name || '',
                  email: apiCompany.email || '',
                  phone: apiCompany.phone || '',
                  website: apiCompany.website || '',
                  street: apiCompany.street || '',
                  postal_code: apiCompany.postal_code || '',
                  city: apiCompany.city || '',
                  logo: apiCompany.logo || null,
                  logo_url: apiCompany.logo_url || (apiCompany.logo ? `${window.location.origin}/storage/${apiCompany.logo}` : null),
                  primary_color: apiCompany.primary_color || '#007bff',
                  notify_admin_on_completion: apiCompany.notify_admin_on_completion === 1,
                  send_applicant_thank_you_email: apiCompany.send_applicant_thank_you_email === 1,
                  // privacy_policy: apiCompany.privacy_policy || ''
                };
                
                // Original-Daten ebenfalls speichern
                originalCompanyData.value = { ...companyData.value };
                
                console.log('Firmendaten direkt aus API geladen:', companyData.value);
              }
            } catch (apiError) {
              console.error('Fehler beim direkten API-Aufruf:', apiError);
            }
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der Firmendaten:', error);
      }
      
      console.log('### CompanySettings onMounted ENDE ###');
    });
    
    // Rückgabe der Refs und Methoden für das Template
    return {
      loading,
      saving,
      companyData,
      logoInput,
      logoPreviewFile,
      logoUpdateKey,
      errors,
      originalCompanyData,
      users,
      userData,
      showUserPassword,
      currentCompany,
      isSuper,
      user,
      userIsSuperAdmin,
      logoPreview,
      websiteWithProtocol,
      isUserFormValid,
      isCurrentlyImpersonating,
      authStore,
      loadCompany,
      saveSettings,
      resetForm,
      clearErrors,
      handleLogoUpload,
      removeLogo,
      darkenColor,
      reloadCompanyData,
      loadCompanyFromData,
      loadDirectFromCurrentUser,
      triggerColorPicker,
    };
  }
});
</script>

<style lang="scss" scoped>
.logo-preview {
  position: relative;
  margin-right: 1rem;
  
  .preview-label {
    position: absolute;
    bottom: -5px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 0.7rem;
    background-color: rgba(var(--primary-color-rgb), 0.8);
    color: white;
    padding: 2px 5px;
    border-radius: 0 0 5px 5px;
  }
}

.company-logo.logo-lg {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

// Zusätzliche Stile für verbessertes Design
.page-container {
  padding-top: 2rem; // Mehr Platz oben
  padding-bottom: 3rem;
}

.display-heading {
  color: var(--bs-gray-800);
}

.btn-subtle {
  color: var(--bs-gray-600);
  &:hover {
    color: var(--bs-primary);
    background-color: var(--bs-light);
  }
}

.card-main-settings {
  border-radius: 1rem; // Größerer Radius
  border: none;
  background-color: var(--bs-body-bg);
  
  // Entferne Header, falls nicht benötigt oder style ihn separat
  :deep(.card-header) {
    display: none; // Beispiel: Header ausblenden
  }
}

.form-enhanced {
  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--bs-primary);
    margin-bottom: 1.25rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid var(--bs-border-color-translucent);
  }

  .form-label {
    font-weight: 500;
    color: var(--bs-gray-700);
    margin-bottom: 0.5rem;
    &.required::after {
      content: ' *';
      color: var(--bs-danger);
    }
  }

  .form-control,
  .form-select {
    border-radius: 0.5rem; // Weichere Ecken
    padding: 0.75rem 1rem;
    border-color: var(--bs-border-color);
    &:focus {
      border-color: var(--bs-primary);
      box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.15);
    }
  }
  
  .form-control-color {
    width: 40px; // Etwas breiter
    height: 40px; // Höhe anpassen an Breite
    padding: 0.25rem; 
    cursor: pointer;
    border-radius: 0.5rem; // Gleicher Radius wie andere Inputs
    border: 1px solid var(--bs-border-color);
    transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
    &:hover {
        transform: scale(1.05);
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }
    &:focus {
        box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.25);
    }
    // Entferne Standard-Rahmen vom Browser
    &::-webkit-color-swatch-wrapper {
        padding: 0;
    }
    &::-webkit-color-swatch {
        border: none;
        border-radius: 0.375rem; // Innerer Radius
    }
    &::-moz-color-swatch {
        border: none;
        border-radius: 0.375rem;
    }
  }

  // Visuell versteckter, aber funktionaler Farb-Input
  .visually-hidden-color-input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      pointer-events: none; // Verhindert direkte Interaktion
  }

  // Angepasster, klickbarer Farb-Swatch
  .custom-color-swatch {
      width: 38px; // Passend zur Höhe der Text-Inputs (ca.)
      height: 38px;
      border-radius: 0.5rem;
      border: 1px solid var(--bs-border-color);
      cursor: pointer;
      display: inline-block;
      vertical-align: middle; // Bessere Ausrichtung mit Text-Input
      transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out;

      &:hover {
          transform: scale(1.05);
          border-color: var(--bs-primary-border-subtle);
          box-shadow: 0 2px 5px rgba(0,0,0,0.1);
      }
      &:focus,
      &:focus-visible { // Fokus-Stil für Barrierefreiheit
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(var(--bs-primary-rgb), 0.25);
          transform: scale(1.05);
      }
  }

  .color-input-group {
    display: flex;
    align-items: center;

    // Textfeld für Hex-Code
    .form-control[type="text"] {
        flex-grow: 1; // Nimmt restlichen Platz ein
        border-radius: 0.5rem; // Einheitlicher Radius
        font-family: var(--bs-font-monospace); // Monospace für Hex-Code
    }
  }

  .logo-preview-container {
    position: relative;
    .preview-label {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      font-size: 0.7rem;
      background-color: rgba(0,0,0, 0.6);
      color: white;
      padding: 2px 5px;
      text-align: center;
      border-radius: 0 0 4px 4px;
    }
    .company-logo {
      width: 80px; // Etwas kleiner
      height: 80px;
    }
  }

  .setting-switch-card {
    background-color: var(--bs-light); // Hellerer Hintergrund
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
    border: 1px solid var(--bs-border-color-translucent);
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: var(--bs-gray-200);
    }

    .form-check {
      // Basis-Layout mit Flexbox
      display: flex;
      justify-content: space-between; // Elemente verteilen
      align-items: center; // Vertikal zentrieren
      padding-left: 0; 
      min-height: auto; 

      // Container für Label und Beschreibung
      > div {
        margin-right: 1rem; // Abstand zum Schalter
        flex-grow: 1; // Nimmt verfügbaren Platz ein
      }
    }

    .form-check-label {
      font-weight: 500;
      cursor: pointer; // Label bleibt klickbar
      margin-bottom: 0.1rem; // Kleiner Abstand zur Beschreibung
    }

    .form-check-input {
      cursor: pointer;
      margin-top: 0; 
      margin-left: 0; // Wichtig: Standard-Margin von Bootstrap entfernen
      flex-shrink: 0; 
      position: relative; // Für korrekte Darstellung des Schalters
      float: none; // Standard-Float aufheben

      // Styling für den aktivierten Zustand
      &:checked {
        background-color: var(--bs-primary); // Klare Farbe für checked
        border-color: var(--bs-primary);
        // Sicherstellen, dass der Knopf (Pseudo-Element) sich bewegt und sichtbar ist
        // Bootstrap 5 verwendet background-image für den Knopf
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e");
      }
    }

    .form-text {
      margin-top: 0; // Kein extra margin-top
      margin-left: 0; // Kein Einrücken mehr nötig
      font-size: 0.8rem;
      line-height: 1.3;
    }
  }
  .form-switch.form-switch-lg .form-check-input {
    height: 1.5rem;
    width: calc(1.5rem + 1.5rem);
  }

  .form-actions {
    // Stile für Aktionsbuttons
  }
}

.card-preview {
  border-radius: 1rem;
  border: 1px solid var(--bs-border-color);
  background-color: var(--bs-body-bg);
  
  :deep(.card-header) {
    background-color: var(--bs-light);
    border-bottom: 1px solid var(--bs-border-color);
    padding: 1rem 1.25rem;
    font-weight: 600;
    h5 {
      font-size: 1rem;
    }
  }

  .preview-content {
    .preview-title {
      font-weight: 600;
      color: var(--bs-gray-800);
    }
    .preview-details {
      font-size: 0.9rem;
      .preview-meta {
        margin-bottom: 0.75rem;
        display: flex;
        align-items: flex-start;
        i {
          margin-top: 0.15rem;
        }
        &.address-preview {
          span {
            display: block;
          }
        }
      }
    }
    .color-sample {
      .color-swatch {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 1px solid var(--bs-border-color);
      }
      .color-hex {
        font-family: var(--bs-font-monospace);
      }
    }
  }
}

.preview-sticky-container {
  top: 2rem; // Abstand von oben für sticky
}

// Responsive Anpassungen bei Bedarf
@media (max-width: 991.98px) {
  .preview-sticky-container {
    position: static; // Sticky auf kleinen Screens deaktivieren
  }
}
</style> 